import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  InputAdornment,
  Pagination,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  Add as AddIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fetchWalletData, resetWalletState } from '../store/slices/walletSlice';
import { formatCurrency } from '../utils/formatters';

/**
 * Wallet Page component
 * Displays user wallet balance and transaction history
 * @returns {JSX.Element} The WalletPage component
 */
const WalletPage = () => {
  const { t } = useTranslation('user');
  const dispatch = useDispatch();
  const { wallet, transactions, pagination, status, error } = useSelector((state) => state.wallet);

  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10); // Items per page

  // Fetch data when component mounts or page changes
  useEffect(() => {
    dispatch(fetchWalletData({ page: currentPage, limit }));

    // Cleanup function to reset state on unmount
    return () => {
      dispatch(resetWalletState());
    };
  }, [dispatch, currentPage, limit]);

  // Handle page change
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Handle refresh
  const handleRefresh = () => {
    setCurrentPage(1); // Reset to page 1
    dispatch(fetchWalletData({ page: 1, limit }));
  };

  // Get transaction type color and icon
  const getTransactionTypeProps = (type) => {
    switch (type) {
      case 'deposit':
      case 'credit': // Treat refund/credit as positive
        return { color: 'success', icon: <ArrowDownwardIcon /> };
      case 'withdrawal':
      case 'debit': // Treat order payment/debit as negative
        return { color: 'error', icon: <ArrowUpwardIcon /> };
      case 'payment':
        return { color: 'warning', icon: <ReceiptIcon /> };
      default:
        return { color: 'default', icon: <AccountBalanceIcon /> };
    }
  };

  // Main Render
  return (
    <Container
      maxWidth="lg"
      sx={{ py: 4 }}
    >
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
      >
        {t('wallet.currentBalance')}
      </Typography>

      {/* Loading and Error States */}
      {status === 'loading' && !wallet && <CircularProgress />}
      {status === 'failed' && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
        >
          {error?.message || t('status.error')}
        </Alert>
      )}

      {/* Wallet Balance Card */}
      {wallet && (
        <Card sx={{ mb: 4, bgcolor: 'primary.main', color: 'white.dark' }}>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6">{t('wallet.currentBalance')}</Typography>
              <AccountBalanceIcon fontSize="large" />
            </Box>
            <Typography
              variant="h3"
              component="p"
              sx={{ mt: 2 }}
            >
              {formatCurrency(wallet.balance, wallet.currency || 'CHF')}
            </Typography>
            <Typography variant="caption">
              {t('wallet.lastUpdated')}:{' '}
              {wallet.updatedAt ? format(new Date(wallet.updatedAt), 'PPpp') : 'N/A'}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Transaction History */}
      <Paper sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6">{t('wallet.transactionHistory')}</Typography>
          <Button
            onClick={handleRefresh}
            startIcon={<RefreshIcon />}
            variant="outlined"
          >
            {t('wallet.refresh')}
          </Button>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {status === 'loading' && transactions.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {status !== 'loading' && transactions.length === 0 ? (
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ textAlign: 'center', py: 3 }}
          >
            {t('wallet.noTransactions')}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('wallet.date')}</TableCell>
                  <TableCell>{t('wallet.type')}</TableCell>
                  <TableCell>{t('wallet.description')}</TableCell>
                  <TableCell align="right">{t('wallet.amount')}</TableCell>
                  <TableCell align="right">{t('wallet.balanceAfter')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((tx) => {
                  const typeProps = getTransactionTypeProps(tx.type);
                  return (
                    <TableRow
                      key={tx.id}
                      hover
                    >
                      <TableCell>{format(new Date(tx.createdAt), 'PP pp')}</TableCell>
                      <TableCell>
                        <Chip
                          icon={typeProps.icon}
                          label={t(`wallet.type_${tx.type}`)}
                          color={typeProps.color}
                          size="small"
                          variant="outlined"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>{tx.description}</TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          color={
                            typeProps.color === 'success'
                              ? 'success.main'
                              : typeProps.color === 'error'
                              ? 'error.main'
                              : 'text.primary'
                          }
                          sx={{ fontWeight: 'bold' }}
                        >
                          {typeProps.color === 'error' ? '-' : '+'}
                          {formatCurrency(tx.amount, tx.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(tx.balanceAfter, tx.currency)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
            <Pagination
              count={pagination.totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default WalletPage;
